'use client';

import React, { useState, useEffect, useMemo } from 'react';
import AppImage from '@/components/ui/AppImage';

interface RSVP {
  _id: string;
  Timestamp: string;
  'Full Name': string;
  Phone: string;
  Email: string;
  'Travelling From': string;
  'Travelling With': string;
  'Guest Count': string;
  Attendance: string;
  Certainty: string;
  'Bhopal Accommodation': string;
  'Bhopal Check-In': string;
  'Bhopal Check-Out': string;
  'Bhopal Rooms': string;
  'Warud Accommodation': string;
  'Warud Check-In': string;
  'Warud Check-Out': string;
  'Warud Rooms': string;
  'Food Preference': string;
  'Dietary Requirements': string;
  'Additional Notes': string;
  'Sangeet Performance'?: string;
  [key: string]: string | undefined;
}

const ATTENDANCE_LABELS: Record<string, { label: string; color: string }> = {
  both: { label: 'Both Events', color: '#9b6b4d' },
  engagement: { label: 'Engagement Only', color: '#c4956a' },
  wedding: { label: 'Wedding Only', color: '#7a9b6b' },
  neither: { label: 'Not Attending', color: '#a0a0a0' },
};

const CERTAINTY_COLORS: Record<string, string> = {
  confirmed: '#7a9b6b',
  likely: '#c4956a',
  unsure: '#a0a0a0',
};

const FOOD_COLORS: Record<string, string> = {
  vegetarian: '#7a9b6b',
  vegan: '#5a8a5a',
  'non-vegetarian': '#c4956a',
  'non vegetarian': '#c4956a',
  jain: '#9b8a6b',
  other: '#a0a0a0',
};

const GROOM_RSVP_FIELD_GROUPS = [
  {
    title: 'Personal Info',
    icon: '👤',
    fields: ['Full Name', 'Phone', 'Email', 'Travelling From', 'Travelling With', 'Guest Count'],
  },
  {
    title: 'Attendance',
    icon: '🎉',
    fields: ['Attendance', 'Certainty'],
  },
  {
    title: 'Bhopal — Engagement',
    icon: '💍',
    fields: [
      'Bhopal Transport', 'Bhopal Arrival Date', 'Bhopal Arrival Time',
      'Bhopal Arrival Point', 'Bhopal Pickup', 'Bhopal Accommodation',
      'Bhopal Check-In', 'Bhopal Check-Out', 'Bhopal Rooms',
    ],
  },
  {
    title: 'Warud — Wedding',
    icon: '🌸',
    fields: [
      'Warud Transport', 'Warud Travel Date', 'Warud Accommodation',
      'Warud Check-In', 'Warud Check-Out', 'Warud Rooms',
    ],
  },
  {
    title: 'Food & Diet',
    icon: '🍽️',
    fields: ['Food Preference', 'Dietary Requirements'],
  },
  {
    title: 'Other',
    icon: '📝',
    fields: ['After Wedding', 'Travel Summary', 'Additional Notes'],
  },
];

const BRIDE_RSVP_FIELD_GROUPS = [
  {
    title: 'Personal Info',
    icon: '👤',
    fields: ['Full Name', 'Phone', 'Travelling From', 'Travelling With', 'Guest Count'],
  },
  {
    title: 'Attendance',
    icon: '🎉',
    fields: ['Attendance', 'Certainty'],
  },
  {
    title: 'Warud — Wedding',
    icon: '🌸',
    fields: [
      'Warud Transport', 'Warud Accommodation',
      'Warud Check-In', 'Warud Check-Out', 'Warud Rooms', 'Warud Room Guests',
    ],
  },
  {
    title: 'Sangeet',
    icon: '🎶',
    fields: ['Sangeet Performance'],
  },
  {
    title: 'Other',
    icon: '📝',
    fields: ['After Wedding', 'Travel Summary', 'Additional Notes'],
  },
];

function StatCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div
      className="rounded-2xl p-5 border flex flex-col gap-1"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
      <p className="text-3xl font-bold" style={{ color: color || 'var(--primary)' }}>
        {value}
      </p>
      <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
        {label}
      </p>
      {sub && (
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          {sub}
        </p>
      )}
    </div>
  );
}

function BarRow({ label, count, total, color }: { label: string; count: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs w-28 shrink-0 truncate" style={{ color: 'var(--foreground)' }}>
        {label}
      </span>
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--muted)' }}>
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-xs w-8 text-right shrink-0" style={{ color: 'var(--muted-foreground)' }}>
        {count}
      </span>
    </div>
  );
}

function SectionCard({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div
      className="rounded-2xl border p-5 space-y-4"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
      <div className="flex items-center gap-2">
        <span className="text-lg">{icon}</span>
        <h2 className="font-semibold text-sm uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
          {title}
        </h2>
      </div>
      {children}
    </div>
  );
}

// ─── Guest Detail Modal ───────────────────────────────────────────────────────

interface GuestDetailModalProps {
  rsvp: RSVP;
  side: 'groom' | 'bride';
  onClose: () => void;
  onSave: (updated: RSVP) => void;
  onDelete: (id: string) => void;
}

function GuestDetailModal({ rsvp, side, onClose, onSave, onDelete }: GuestDetailModalProps) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<RSVP>({ ...rsvp });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fieldGroups = side === 'bride' ? BRIDE_RSVP_FIELD_GROUPS : GROOM_RSVP_FIELD_GROUPS;

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const endpoint = side === 'bride' ? `/api/admin/bride-rsvps/${rsvp._id}` : `/api/admin/rsvps/${rsvp._id}`;
      const res = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      onSave(form);
      setEditing(false);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setSaveError(null);
    try {
      const endpoint = side === 'bride' ? `/api/admin/bride-rsvps/${rsvp._id}` : `/api/admin/rsvps/${rsvp._id}`;
      const res = await fetch(endpoint, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete');
      onDelete(rsvp._id);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Delete failed');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}>
        {/* Modal Header */}
        <div
          className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
          <div>
            <h2 className="font-semibold text-lg" style={{ color: 'var(--foreground)' }}>
              {rsvp['Full Name'] || 'Guest Details'}
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
              Submitted: {rsvp['Timestamp'] || '—'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!editing ? (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-opacity hover:opacity-80"
                  style={{ backgroundColor: 'var(--primary)', color: '#fff' }}>
                  ✏️ Edit
                </button>
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-opacity hover:opacity-80"
                  style={{ backgroundColor: '#fee2e2', color: '#c0392b', border: '1px solid #fca5a5' }}>
                  🗑️ Remove
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-50"
                  style={{ backgroundColor: '#7a9b6b', color: '#fff' }}>
                  {saving ? 'Saving…' : '✓ Save'}
                </button>
                <button
                  onClick={() => { setEditing(false); setForm({ ...rsvp }); setSaveError(null); }}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-opacity hover:opacity-80"
                  style={{ backgroundColor: 'var(--muted)', color: 'var(--foreground)', border: '1px solid var(--border)' }}>
                  Cancel
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full transition-opacity hover:opacity-70"
              style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>
              ✕
            </button>
          </div>
        </div>

        {/* Error */}
        {saveError && (
          <div className="mx-6 mt-4 px-4 py-3 rounded-lg text-sm" style={{ backgroundColor: '#fee2e2', color: '#c0392b' }}>
            {saveError}
          </div>
        )}

        {/* Confirm Delete */}
        {confirmDelete && (
          <div className="mx-6 mt-4 px-4 py-4 rounded-xl border" style={{ backgroundColor: '#fff5f5', borderColor: '#fca5a5' }}>
            <p className="text-sm font-medium mb-3" style={{ color: '#c0392b' }}>
              Remove <strong>{rsvp['Full Name']}</strong>'s RSVP entry? This cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: '#c0392b', color: '#fff' }}>
                {deleting ? 'Removing…' : 'Yes, Remove'}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium"
                style={{ backgroundColor: 'var(--muted)', color: 'var(--foreground)', border: '1px solid var(--border)' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Field Groups */}
        <div className="px-6 py-4 space-y-5">
          {fieldGroups.map((group) => {
            const visibleFields = group.fields.filter((f) => editing || form[f]);
            if (!editing && visibleFields.length === 0) return null;
            return (
              <div key={group.title}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-base">{group.icon}</span>
                  <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
                    {group.title}
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {group.fields.map((field) => {
                    const value = form[field] || '';
                    if (!editing && !value) return null;
                    return (
                      <div key={field}>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>
                          {field}
                        </label>
                        {editing ? (
                          field === 'Additional Notes' || field === 'Travel Summary' || field === 'Dietary Requirements' ? (
                            <textarea
                              value={value}
                              onChange={(e) => handleChange(field, e.target.value)}
                              rows={2}
                              className="w-full rounded-lg px-3 py-2 text-sm border outline-none focus:ring-2 resize-none"
                              style={{
                                backgroundColor: 'var(--muted)',
                                borderColor: 'var(--border)',
                                color: 'var(--foreground)',
                              }}
                            />
                          ) : field === 'Attendance' ? (
                            <select
                              value={value}
                              onChange={(e) => handleChange(field, e.target.value)}
                              className="w-full rounded-lg px-3 py-2 text-sm border outline-none"
                              style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                              <option value="">—</option>
                              <option value="both">Both Events</option>
                              <option value="engagement">Engagement Only</option>
                              <option value="wedding">Wedding Only</option>
                              <option value="neither">Not Attending</option>
                            </select>
                          ) : field === 'Certainty' ? (
                            <select
                              value={value}
                              onChange={(e) => handleChange(field, e.target.value)}
                              className="w-full rounded-lg px-3 py-2 text-sm border outline-none"
                              style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                              <option value="">—</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="likely">Likely</option>
                              <option value="unsure">Unsure</option>
                            </select>
                          ) : field === 'Food Preference' ? (
                            <select
                              value={value}
                              onChange={(e) => handleChange(field, e.target.value)}
                              className="w-full rounded-lg px-3 py-2 text-sm border outline-none"
                              style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                              <option value="">—</option>
                              <option value="Vegetarian">Vegetarian</option>
                              <option value="Vegan">Vegan</option>
                              <option value="Non-Vegetarian">Non-Vegetarian</option>
                              <option value="Jain">Jain</option>
                              <option value="Other">Other</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={value}
                              onChange={(e) => handleChange(field, e.target.value)}
                              className="w-full rounded-lg px-3 py-2 text-sm border outline-none focus:ring-2"
                              style={{
                                backgroundColor: 'var(--muted)',
                                borderColor: 'var(--border)',
                                color: 'var(--foreground)',
                              }}
                            />
                          )
                        ) : (
                          <p className="text-sm" style={{ color: 'var(--foreground)' }}>
                            {value || '—'}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function useRSVPData(endpoint: string, isAuthenticated: boolean) {
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    async function fetchRSVPs() {
      try {
        const res = await fetch(endpoint);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to fetch RSVPs');
        setRsvps(data.rsvps);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    }
    fetchRSVPs();
  }, [isAuthenticated, endpoint]);

  return { rsvps, setRsvps, loading, error };
}

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [authError, setAuthError] = useState('');
  const [sideTab, setSideTab] = useState<'groom' | 'bride'>('groom');
  const [activeTab, setActiveTab] = useState<'analytics' | 'guests'>('analytics');
  const [selectedGuest, setSelectedGuest] = useState<RSVP | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [attendanceFilter, setAttendanceFilter] = useState('all');
  const [certaintyFilter, setCertaintyFilter] = useState('all');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const ACCESS_CODE = 'VimiWedding2025';

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (accessCode === ACCESS_CODE) {
      setIsAuthenticated(true);
      setAuthError('');
    } else {
      setAuthError('Incorrect access code. Please try again.');
      setAccessCode('');
    }
  };

  const { rsvps: groomRsvps, setRsvps: setGroomRsvps, loading: groomLoading, error: groomError } = useRSVPData('/api/admin/rsvps', isAuthenticated);
  const { rsvps: brideRsvps, setRsvps: setBrideRsvps, loading: brideLoading, error: brideError } = useRSVPData('/api/admin/bride-rsvps', isAuthenticated);

  const rsvps = sideTab === 'bride' ? brideRsvps : groomRsvps;
  const setRsvps = sideTab === 'bride' ? setBrideRsvps : setGroomRsvps;
  const loading = sideTab === 'bride' ? brideLoading : groomLoading;
  const error = sideTab === 'bride' ? brideError : groomError;

  const handleGuestSave = (updated: RSVP) => {
    setRsvps((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
    setSelectedGuest(updated);
  };

  const handleGuestDelete = (id: string) => {
    setRsvps((prev) => prev.filter((r) => r._id !== id));
    setSelectedGuest(null);
  };

  const handleDownloadCSV = () => {
    if (rsvps.length === 0) return;
    const excludeKeys = ['_id'];
    const allKeys = Array.from(
      new Set(rsvps.flatMap((r) => Object.keys(r).filter((k) => !excludeKeys.includes(k))))
    );
    const escapeCSV = (val: string | undefined) => {
      if (val === undefined || val === null) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const header = allKeys.map(escapeCSV).join(',');
    const rows = rsvps.map((r) => allKeys.map((k) => escapeCSV(r[k])).join(','));
    const csvContent = [header, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const fileName = `${sideTab}-rsvps-${new Date().toISOString().slice(0, 10)}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const analytics = useMemo(() => {
    const total = rsvps.length;
    const totalGuests = rsvps.reduce((sum, r) => sum + (parseInt(r['Guest Count'] || '1') || 1), 0);

    const attendanceCounts: Record<string, number> = { both: 0, engagement: 0, wedding: 0, neither: 0 };
    rsvps.forEach((r) => {
      const key = r['Attendance']?.toLowerCase() || 'neither';
      if (key in attendanceCounts) attendanceCounts[key]++;
      else attendanceCounts['neither']++;
    });

    const certaintyCounts: Record<string, number> = { confirmed: 0, likely: 0, unsure: 0 };
    rsvps.forEach((r) => {
      const key = r['Certainty']?.toLowerCase() || 'unsure';
      if (key in certaintyCounts) certaintyCounts[key]++;
      else certaintyCounts['unsure']++;
    });

    const bhopalAttending = rsvps.filter(
      (r) => r['Attendance']?.toLowerCase() === 'both' || r['Attendance']?.toLowerCase() === 'engagement'
    );
    const bhopalGuests = bhopalAttending.reduce((sum, r) => sum + (parseInt(r['Guest Count'] || '1') || 1), 0);
    const bhopalAccomm: Record<string, number> = {};
    bhopalAttending.forEach((r) => {
      const acc = r['Bhopal Accommodation'] || 'Not specified';
      bhopalAccomm[acc] = (bhopalAccomm[acc] || 0) + 1;
    });
    const bhopalRooms = bhopalAttending.reduce((sum, r) => sum + (parseInt(r['Bhopal Rooms'] || '0') || 0), 0);
    const bhopalPickup = rsvps.filter((r) => r['Bhopal Pickup']?.toLowerCase() === 'yes').length;

    const warudAttending = rsvps.filter(
      (r) => r['Attendance']?.toLowerCase() === 'both' || r['Attendance']?.toLowerCase() === 'wedding'
    );
    const warudGuests = warudAttending.reduce((sum, r) => sum + (parseInt(r['Guest Count'] || '1') || 1), 0);
    const warudAccomm: Record<string, number> = {};
    warudAttending.forEach((r) => {
      const acc = r['Warud Accommodation'] || 'Not specified';
      warudAccomm[acc] = (warudAccomm[acc] || 0) + 1;
    });
    const warudRooms = warudAttending.reduce((sum, r) => sum + (parseInt(r['Warud Rooms'] || '0') || 0), 0);

    const foodCounts: Record<string, number> = {};
    rsvps.forEach((r) => {
      const food = r['Food Preference']?.trim() || 'Not specified';
      const key = food.toLowerCase();
      foodCounts[key] = (foodCounts[key] || 0) + 1;
    });

    const dietaryList = rsvps
      .filter((r) => r['Dietary Requirements']?.trim())
      .map((r) => ({ name: r['Full Name'], req: r['Dietary Requirements'] || '', rsvp: r }));

    const originCounts: Record<string, number> = {};
    rsvps.forEach((r) => {
      const origin = r['Travelling From']?.trim() || 'Unknown';
      originCounts[origin] = (originCounts[origin] || 0) + 1;
    });
    const topOrigins = Object.entries(originCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    const afterWeddingCounts: Record<string, number> = {};
    rsvps.forEach((r) => {
      const aw = r['After Wedding']?.trim() || 'Not specified';
      afterWeddingCounts[aw] = (afterWeddingCounts[aw] || 0) + 1;
    });

    // Bride-specific: Sangeet performance
    const sangeetCounts: Record<string, number> = {};
    if (sideTab === 'bride') {
      rsvps.forEach((r) => {
        const sp = r['Sangeet Performance']?.trim() || 'Not specified';
        sangeetCounts[sp] = (sangeetCounts[sp] || 0) + 1;
      });
    }

    return {
      total,
      totalGuests,
      attendanceCounts,
      certaintyCounts,
      bhopalAttending: bhopalAttending.length,
      bhopalGuests,
      bhopalAccomm,
      bhopalRooms,
      bhopalPickup,
      warudAttending: warudAttending.length,
      warudGuests,
      warudAccomm,
      warudRooms,
      foodCounts,
      dietaryList,
      topOrigins,
      afterWeddingCounts,
      sangeetCounts,
    };
  }, [rsvps, sideTab]);

  const filtered = useMemo(() => {
    return rsvps.filter((r) => {
      const name = r['Full Name']?.toLowerCase() || '';
      const phone = r['Phone'] || '';
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery || name.includes(query) || phone.includes(query);
      const matchesAttendance =
        attendanceFilter === 'all' || r['Attendance']?.toLowerCase() === attendanceFilter;
      const matchesCertainty =
        certaintyFilter === 'all' || r['Certainty']?.toLowerCase() === certaintyFilter;
      return matchesSearch && matchesAttendance && matchesCertainty;
    });
  }, [rsvps, searchQuery, attendanceFilter, certaintyFilter]);

  const getAttendanceBadge = (attendance: string) => {
    const key = attendance?.toLowerCase();
    const config = ATTENDANCE_LABELS[key] || { label: attendance || '—', color: '#a0a0a0' };
    return (
      <span
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
        style={{ backgroundColor: config.color + '20', color: config.color, border: `1px solid ${config.color}40` }}>
        {config.label}
      </span>
    );
  };

  const getCertaintyBadge = (certainty: string) => {
    const key = certainty?.toLowerCase();
    const color = CERTAINTY_COLORS[key] || '#a0a0a0';
    const label = certainty ? certainty.charAt(0).toUpperCase() + certainty.slice(1) : '—';
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color }}>
        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: color }} />
        {label}
      </span>
    );
  };

  if (!isAuthenticated) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#faf8f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Georgia, serif' }}>
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '48px 40px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', maxWidth: '400px', width: '100%', textAlign: 'center' }}>
          <div style={{ marginBottom: '8px', fontSize: '28px' }}>💍</div>
          <h1 style={{ fontSize: '22px', fontWeight: '600', color: '#5c3d2e', marginBottom: '6px' }}>Admin Access</h1>
          <p style={{ fontSize: '14px', color: '#9b7b6b', marginBottom: '32px' }}>Enter the access code to view RSVP data</p>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Access code"
              autoFocus
              style={{ width: '100%', padding: '12px 16px', border: '1.5px solid #e8d5c4', borderRadius: '8px', fontSize: '15px', color: '#5c3d2e', backgroundColor: '#faf8f5', outline: 'none', boxSizing: 'border-box', marginBottom: '12px' }}
            />
            {authError && <p style={{ color: '#c0392b', fontSize: '13px', marginBottom: '12px' }}>{authError}</p>}
            <button
              type="submit"
              style={{ width: '100%', padding: '12px', backgroundColor: '#9b6b4d', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
              View RSVPs
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
      {/* Guest Detail Modal */}
      {selectedGuest && (
        <GuestDetailModal
          rsvp={selectedGuest}
          side={sideTab}
          onClose={() => setSelectedGuest(null)}
          onSave={handleGuestSave}
          onDelete={handleGuestDelete}
        />
      )}

      {/* Header */}
      <div className="sticky top-0 z-10 border-b shadow-sm" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <AppImage
            src="/assets/images/Logo_Vimi-1786971272280.png"
            alt="Vimi logo"
            width={32}
            height={32}
            className="object-contain"
            unoptimized={true}
            style={{ width: '32px', height: '32px' }}
          />
          <div>
            <h1 className="font-display text-xl font-medium" style={{ color: 'var(--foreground)' }}>
              RSVP Admin
            </h1>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Vikas &amp; Anamika — Guest Management
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {!loading && !error && (
              <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: 'var(--primary)' + '15', color: 'var(--primary)' }}>
                {analytics.total} RSVPs · {analytics.totalGuests} guests
              </span>
            )}
            {!loading && !error && rsvps.length > 0 && (
              <button
                onClick={handleDownloadCSV}
                className="text-xs px-3 py-1.5 rounded-lg border font-medium transition-opacity hover:opacity-80 flex items-center gap-1"
                style={{ backgroundColor: '#7a9b6b', color: '#fff', borderColor: '#7a9b6b' }}>
                ⬇ Export CSV
              </button>
            )}
            <a href="/" className="text-xs px-3 py-1.5 rounded-lg border transition-colors hover:opacity-80" style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}>
              ← Home
            </a>
          </div>
        </div>

        {/* Groom / Bride side tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1 border-b" style={{ borderColor: 'var(--border)' }}>
          {(['groom', 'bride'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => { setSideTab(tab); setSelectedGuest(null); setSearchQuery(''); setAttendanceFilter('all'); setCertaintyFilter('all'); }}
              className="px-5 py-2.5 text-sm font-semibold capitalize border-b-2 transition-colors"
              style={{
                borderBottomColor: sideTab === tab ? 'var(--primary)' : 'transparent',
                color: sideTab === tab ? 'var(--primary)' : 'var(--muted-foreground)',
                backgroundColor: 'transparent',
              }}>
              {tab === 'groom' ? '💍 Groom' : '🌸 Bride'}
            </button>
          ))}
        </div>

        {/* Analytics / Guests sub-tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1 pb-0">
          {(['analytics', 'guests'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="px-4 py-2.5 text-sm font-medium capitalize border-b-2 transition-colors"
              style={{
                borderBottomColor: activeTab === tab ? 'var(--secondary)' : 'transparent',
                color: activeTab === tab ? 'var(--secondary)' : 'var(--muted-foreground)',
                backgroundColor: 'transparent',
              }}>
              {tab === 'analytics' ? '📊 Analytics' : '👥 Guest List'}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="p-16 text-center space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 rounded-xl max-w-2xl mx-auto" style={{ backgroundColor: 'var(--muted)' }} />
            ))}
            <p className="text-sm mt-4" style={{ color: 'var(--muted-foreground)' }}>Loading data from Google Sheets…</p>
          </div>
        ) : error ? (
          <div className="p-16 text-center">
            <p className="text-sm font-medium mb-1" style={{ color: '#c0392b' }}>Failed to load data</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{error}</p>
          </div>
        ) : activeTab === 'analytics' ? (
          <AnalyticsView analytics={analytics} rsvps={rsvps} sideTab={sideTab} onSelectGuest={setSelectedGuest} />
        ) : (
          <GuestListView
            rsvps={rsvps}
            filtered={filtered}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            attendanceFilter={attendanceFilter}
            setAttendanceFilter={setAttendanceFilter}
            certaintyFilter={certaintyFilter}
            setCertaintyFilter={setCertaintyFilter}
            expandedRow={expandedRow}
            setExpandedRow={setExpandedRow}
            getAttendanceBadge={getAttendanceBadge}
            getCertaintyBadge={getCertaintyBadge}
            onSelectGuest={setSelectedGuest}
            onDownloadCSV={handleDownloadCSV}
          />
        )}
      </div>
    </div>
  );
}

// ─── Analytics View ───────────────────────────────────────────────────────────

interface AnalyticsData {
  total: number;
  totalGuests: number;
  attendanceCounts: Record<string, number>;
  certaintyCounts: Record<string, number>;
  bhopalAttending: number;
  bhopalGuests: number;
  bhopalAccomm: Record<string, number>;
  bhopalRooms: number;
  bhopalPickup: number;
  warudAttending: number;
  warudGuests: number;
  warudAccomm: Record<string, number>;
  warudRooms: number;
  foodCounts: Record<string, number>;
  dietaryList: { name: string; req: string; rsvp: RSVP }[];
  topOrigins: [string, number][];
  afterWeddingCounts: Record<string, number>;
  sangeetCounts: Record<string, number>;
}

function AnalyticsView({ analytics, rsvps, sideTab, onSelectGuest }: { analytics: AnalyticsData; rsvps: RSVP[]; sideTab: 'groom' | 'bride'; onSelectGuest: (r: RSVP) => void }) {
  if (rsvps.length === 0) {
    return (
      <div className="p-16 text-center">
        <p className="text-4xl mb-3">📋</p>
        <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>No RSVPs yet</p>
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Analytics will appear once guests start submitting RSVPs.</p>
      </div>
    );
  }

  const totalAttending = analytics.attendanceCounts.both + analytics.attendanceCounts.engagement + analytics.attendanceCounts.wedding;

  return (
    <div className="space-y-6">
      {/* Top summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total RSVPs" value={analytics.total} sub="Families / groups" color="var(--primary)" />
        <StatCard label="Total Guests" value={analytics.totalGuests} sub="Including +1s" color="#9b6b4d" />
        <StatCard label="Attending" value={totalAttending} sub={`${analytics.certaintyCounts.confirmed} confirmed`} color="#7a9b6b" />
        <StatCard label="Not Attending" value={analytics.attendanceCounts.neither} sub="Declined" color="#a0a0a0" />
      </div>

      {/* Two-column: Attendance + Certainty */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SectionCard title="Event Attendance" icon="🎉">
          <div className="space-y-3">
            <BarRow label="Both Events" count={analytics.attendanceCounts.both} total={analytics.total} color="#9b6b4d" />
            <BarRow label="Engagement Only" count={analytics.attendanceCounts.engagement} total={analytics.total} color="#c4956a" />
            <BarRow label="Wedding Only" count={analytics.attendanceCounts.wedding} total={analytics.total} color="#7a9b6b" />
            <BarRow label="Not Attending" count={analytics.attendanceCounts.neither} total={analytics.total} color="#d0d0d0" />
          </div>
        </SectionCard>

        <SectionCard title="Confirmation Status" icon="✅">
          <div className="space-y-3">
            <BarRow label="Confirmed" count={analytics.certaintyCounts.confirmed} total={analytics.total} color="#7a9b6b" />
            <BarRow label="Likely" count={analytics.certaintyCounts.likely} total={analytics.total} color="#c4956a" />
            <BarRow label="Unsure" count={analytics.certaintyCounts.unsure} total={analytics.total} color="#d0d0d0" />
          </div>
          <div className="mt-3 pt-3 border-t grid grid-cols-3 gap-2" style={{ borderColor: 'var(--border)' }}>
            {[
              { label: 'Confirmed', val: analytics.certaintyCounts.confirmed, color: '#7a9b6b' },
              { label: 'Likely', val: analytics.certaintyCounts.likely, color: '#c4956a' },
              { label: 'Unsure', val: analytics.certaintyCounts.unsure, color: '#a0a0a0' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-xl font-bold" style={{ color: s.color }}>{s.val}</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {sideTab === 'groom' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SectionCard title="Bhopal — Engagement" icon="💍">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-xl p-3 text-center" style={{ backgroundColor: 'var(--muted)' }}>
                <p className="text-2xl font-bold" style={{ color: '#c4956a' }}>{analytics.bhopalAttending}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>RSVPs</p>
              </div>
              <div className="rounded-xl p-3 text-center" style={{ backgroundColor: 'var(--muted)' }}>
                <p className="text-2xl font-bold" style={{ color: '#c4956a' }}>{analytics.bhopalGuests}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Guests</p>
              </div>
              <div className="rounded-xl p-3 text-center" style={{ backgroundColor: 'var(--muted)' }}>
                <p className="text-2xl font-bold" style={{ color: '#c4956a' }}>{analytics.bhopalRooms}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Rooms</p>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Warud — Wedding" icon="🌸">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-xl p-3 text-center" style={{ backgroundColor: 'var(--muted)' }}>
                <p className="text-2xl font-bold" style={{ color: '#7a9b6b' }}>{analytics.warudAttending}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>RSVPs</p>
              </div>
              <div className="rounded-xl p-3 text-center" style={{ backgroundColor: 'var(--muted)' }}>
                <p className="text-2xl font-bold" style={{ color: '#7a9b6b' }}>{analytics.warudGuests}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Guests</p>
              </div>
              <div className="rounded-xl p-3 text-center" style={{ backgroundColor: 'var(--muted)' }}>
                <p className="text-2xl font-bold" style={{ color: '#7a9b6b' }}>{analytics.warudRooms}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Rooms</p>
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {sideTab === 'bride' && Object.keys(analytics.sangeetCounts).length > 0 && (
        <SectionCard title="Sangeet Performances" icon="🎶">
          <div className="space-y-2">
            {Object.entries(analytics.sangeetCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([perf, cnt]) => (
                <BarRow key={perf} label={perf} count={cnt} total={analytics.total} color="#c9a96e" />
              ))}
          </div>
        </SectionCard>
      )}

      {/* Food & Dietary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SectionCard title="Food Preferences" icon="🍽️">
          <div className="space-y-2">
            {Object.entries(analytics.foodCounts).length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>No food preference data yet</p>
            ) : (
              Object.entries(analytics.foodCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([food, cnt]) => {
                  const color = FOOD_COLORS[food] || '#a0a0a0';
                  const label = food.charAt(0).toUpperCase() + food.slice(1);
                  return <BarRow key={food} label={label} count={cnt} total={analytics.total} color={color} />;
                })
            )}
          </div>
        </SectionCard>

        <SectionCard title="Dietary Requirements" icon="⚠️">
          {analytics.dietaryList.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>No special dietary requirements noted</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {analytics.dietaryList.map((d, i) => (
                <button
                  key={i}
                  onClick={() => onSelectGuest(d.rsvp)}
                  className="w-full text-left rounded-lg px-3 py-2 transition-opacity hover:opacity-80 cursor-pointer"
                  style={{ backgroundColor: 'var(--muted)', border: '1px solid transparent' }}>
                  <p className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>{d.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{d.req}</p>
                </button>
              ))}
            </div>
          )}
          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              <span className="font-semibold" style={{ color: 'var(--foreground)' }}>{analytics.dietaryList.length}</span> guest{analytics.dietaryList.length !== 1 ? 's' : ''} with special requirements
            </p>
          </div>
        </SectionCard>
      </div>

      {/* Travel Origins + After Wedding */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SectionCard title="Travelling From" icon="✈️">
          <div className="space-y-2">
            {analytics.topOrigins.length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>No travel data yet</p>
            ) : (
              analytics.topOrigins.map(([origin, cnt]) => (
                <BarRow key={origin} label={origin} count={cnt} total={analytics.total} color="#9b6b4d" />
              ))
            )}
          </div>
        </SectionCard>

        <SectionCard title="After Wedding Plans" icon="🌅">
          <div className="space-y-2">
            {Object.entries(analytics.afterWeddingCounts).length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>No after-wedding data yet</p>
            ) : (
              Object.entries(analytics.afterWeddingCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([plan, cnt]) => (
                  <BarRow key={plan} label={plan} count={cnt} total={analytics.total} color="#7a9b6b" />
                ))
            )}
          </div>
        </SectionCard>
      </div>

      {/* Clickable Guest List in Analytics */}
      <SectionCard title="All Guests — Click to View / Edit" icon="👥">
        <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
          {rsvps.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>No guests yet.</p>
          ) : (
            rsvps.map((r, idx) => {
              const attKey = r['Attendance']?.toLowerCase();
              const attConfig = ATTENDANCE_LABELS[attKey || ''] || { label: r['Attendance'] || '—', color: '#a0a0a0' };
              return (
                <button
                  key={r._id}
                  onClick={() => onSelectGuest(r)}
                  className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:opacity-80 cursor-pointer"
                  style={{ backgroundColor: 'var(--muted)' }}>
                  <span className="text-xs w-5 shrink-0 text-right" style={{ color: 'var(--muted-foreground)' }}>{idx + 1}</span>
                  <span className="flex-1 text-sm font-medium truncate" style={{ color: 'var(--foreground)' }}>{r['Full Name'] || '—'}</span>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full shrink-0"
                    style={{ backgroundColor: attConfig.color + '20', color: attConfig.color }}>
                    {attConfig.label}
                  </span>
                  <span className="text-xs shrink-0" style={{ color: 'var(--muted-foreground)' }}>
                    {r['Guest Count'] || '1'} guest{parseInt(r['Guest Count'] || '1') !== 1 ? 's' : ''}
                  </span>
                  <span className="text-xs shrink-0" style={{ color: 'var(--muted-foreground)' }}>→</span>
                </button>
              );
            })
          )}
        </div>
      </SectionCard>
    </div>
  );
}

// ─── Guest List View ──────────────────────────────────────────────────────────

interface GuestListViewProps {
  rsvps: RSVP[];
  filtered: RSVP[];
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  attendanceFilter: string;
  setAttendanceFilter: (v: string) => void;
  certaintyFilter: string;
  setCertaintyFilter: (v: string) => void;
  expandedRow: string | null;
  setExpandedRow: (v: string | null) => void;
  getAttendanceBadge: (a: string) => React.ReactNode;
  getCertaintyBadge: (c: string) => React.ReactNode;
  onSelectGuest: (r: RSVP) => void;
  onDownloadCSV?: () => void;
}

function GuestListView({
  rsvps, filtered, searchQuery, setSearchQuery,
  attendanceFilter, setAttendanceFilter,
  certaintyFilter, setCertaintyFilter,
  expandedRow, setExpandedRow,
  getAttendanceBadge, getCertaintyBadge,
  onSelectGuest,
  onDownloadCSV,
}: GuestListViewProps) {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="rounded-xl border p-4 flex flex-col sm:flex-row gap-3" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
        <input
          type="text"
          placeholder="Search by name or phone…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 rounded-lg px-3 py-2 text-sm border outline-none"
          style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
        />
        <select
          value={attendanceFilter}
          onChange={(e) => setAttendanceFilter(e.target.value)}
          className="rounded-lg px-3 py-2 text-sm border outline-none"
          style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}>
          <option value="all">All Events</option>
          <option value="both">Both Events</option>
          <option value="engagement">Engagement Only</option>
          <option value="wedding">Wedding Only</option>
          <option value="neither">Not Attending</option>
        </select>
        <select
          value={certaintyFilter}
          onChange={(e) => setCertaintyFilter(e.target.value)}
          className="rounded-lg px-3 py-2 text-sm border outline-none"
          style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}>
          <option value="all">All Certainty</option>
          <option value="confirmed">Confirmed</option>
          <option value="likely">Likely</option>
          <option value="unsure">Unsure</option>
        </select>
        {rsvps.length > 0 && onDownloadCSV && (
          <button
            onClick={onDownloadCSV}
            className="rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-80 flex items-center gap-1.5 shrink-0"
            style={{ backgroundColor: '#7a9b6b', color: '#fff' }}>
            ⬇ Export CSV
          </button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              {rsvps.length === 0 ? 'No RSVPs submitted yet.' : 'No results match your filters.'}
            </p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {filtered.map((rsvp, idx) => (
              <div
                key={rsvp._id}
                className="p-4 cursor-pointer"
                style={{ backgroundColor: expandedRow === rsvp._id ? 'var(--accent)' : 'transparent' }}
                onClick={() => setExpandedRow(expandedRow === rsvp._id ? null : rsvp._id)}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <span className="text-xs mr-2" style={{ color: 'var(--muted-foreground)' }}>#{idx + 1}</span>
                    <span className="font-medium text-sm" style={{ color: 'var(--foreground)' }}>{rsvp['Full Name'] || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {getAttendanceBadge(rsvp['Attendance'] || '')}
                    <button
                      onClick={(e) => { e.stopPropagation(); onSelectGuest(rsvp); }}
                      className="text-xs px-2 py-0.5 rounded-lg"
                      style={{ backgroundColor: 'var(--primary)' + '15', color: 'var(--primary)' }}>
                      View
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  <span>{rsvp['Phone'] || '—'}</span>
                  <span>·</span>
                  {getCertaintyBadge(rsvp['Certainty'] || '')}
                  <span>·</span>
                  <span>{rsvp['Guest Count'] || '1'} guest{parseInt(rsvp['Guest Count'] || '1') !== 1 ? 's' : ''}</span>
                </div>
                {expandedRow === rsvp._id && (
                  <div className="mt-3 rounded-lg p-3" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mb-3">
                      {Object.entries(rsvp)
                        .filter(([k, v]) => k !== '_id' && k !== 'Timestamp' && v)
                        .map(([k, v]) => (
                          <div key={k}>
                            <p className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>{k}</p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--foreground)' }}>{v}</p>
                          </div>
                        ))}
                    </div>
                    <button
                      onClick={() => onSelectGuest(rsvp)}
                      className="text-xs px-3 py-1.5 rounded-lg"
                      style={{ backgroundColor: 'var(--primary)', color: '#fff' }}>
                      ✏️ Edit / Manage
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <p className="text-xs text-center pb-4" style={{ color: 'var(--muted-foreground)' }}>
          Showing {filtered.length} of {rsvps.length} RSVPs
        </p>
      )}
    </div>
  );
}
